import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: '', category: 'business', subcategory: 'real_estate', useCase: '', description: '',
  bottle: 'dark', displayOrder: 0, featured: false, active: true, link: '',
};

const Field = ({ label, children }) => (
  <label className="block text-sm">
    <span className="block text-xs font-semibold text-gray-600 mb-1">{label}</span>
    {children}
  </label>
);

const cls = 'w-full border rounded-md px-3 py-2 text-sm';

const PortfolioForm = ({ subcategories, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const up = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setCategory = (c) => setForm((p) => ({ ...p, category: c, subcategory: subcategories[c]?.[0]?.value || '' }));

  const onFile = (f) => {
    setFile(f);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(f ? URL.createObjectURL(f) : null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast({ title: 'Choose a label file first', variant: 'destructive' });
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
      fd.append('label', file);
      await axios.post(`${API}/portfolio/`, fd, { headers: { Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` } });
      toast({ title: 'Added to showcase', description: `${form.name} rendered on the ${form.bottle} bottle` });
      setForm(EMPTY); onFile(null); onSaved();
    } catch (err) {
      toast({ title: 'Upload failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-lg border p-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="portfolio-form">
      <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Partner / client name *">
          <input className={cls} required value={form.name} onChange={(e) => up('name', e.target.value)} data-testid="pf-name" />
        </Field>
        <Field label="Category">
          <select className={cls} value={form.category} onChange={(e) => setCategory(e.target.value)} data-testid="pf-category">
            <option value="business">Business</option>
            <option value="event">Wedding / Event</option>
          </select>
        </Field>
        <Field label="Industry / subcategory">
          <select className={cls} value={form.subcategory} onChange={(e) => up('subcategory', e.target.value)} data-testid="pf-subcategory">
            {(subcategories[form.category] || []).map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Bottle">
          <select className={cls} value={form.bottle} onChange={(e) => up('bottle', e.target.value)} data-testid="pf-bottle">
            <option value="dark">Dark (red)</option>
            <option value="clear">Clear (white)</option>
          </select>
        </Field>
        <Field label="Use case (short)">
          <input className={cls} placeholder="Custom closing & client appreciation wine" value={form.useCase} onChange={(e) => up('useCase', e.target.value)} data-testid="pf-usecase" />
        </Field>
        <Field label="Website / social link (optional)">
          <input className={cls} placeholder="https://" value={form.link} onChange={(e) => up('link', e.target.value)} data-testid="pf-link" />
        </Field>
        <Field label="Short description (optional)">
          <input className={cls} value={form.description} onChange={(e) => up('description', e.target.value)} data-testid="pf-description" />
        </Field>
        <div className="grid grid-cols-3 gap-2 items-end">
          <Field label="Order">
            <input type="number" className={cls} value={form.displayOrder} onChange={(e) => up('displayOrder', Number(e.target.value))} data-testid="pf-order" />
          </Field>
          <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={form.featured} onChange={(e) => up('featured', e.target.checked)} data-testid="pf-featured" /> Featured</label>
          <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={form.active} onChange={(e) => up('active', e.target.checked)} data-testid="pf-active" /> Active</label>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <Field label="Label artwork * (PNG, JPG, WEBP or SVG)">
          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="text-sm" onChange={(e) => onFile(e.target.files?.[0] || null)} data-testid="pf-file" />
        </Field>
        <div className="flex-1 min-h-[120px] border rounded-md bg-gray-50 flex items-center justify-center overflow-hidden">
          {preview ? <img src={preview} alt="label preview" className="max-h-40 object-contain" /> : <span className="text-xs text-gray-400">Label preview — orientation detected on upload</span>}
        </div>
        <Button type="submit" disabled={saving} data-testid="pf-submit">{saving ? 'Rendering bottle…' : 'Add to showcase'}</Button>
      </div>
    </form>
  );
};

export default PortfolioForm;
